import React, { useState } from 'react';
import RequestAssetForm from './RequestAssetForm';
import { postAssetRequest } from '../../api/assetsApi';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAssetRequests } from '../../redux/assets/actions';
import { toast } from 'react-toastify';
import { CREATE_ASSET_REQUEST_TOAST_MESSAGES } from '../../service/toastService';
import Modal from '../common/Modal';

function RequestAssetModal({ show, onClose }) {
  const dispatch = useDispatch();
  const { selectedUser } = useSelector((state) => state.users);
  const { takeRequests } = useSelector((state) => state.assets);
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    startingFrom: '',
    price: '',
    motivation: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  if (!show && !showConfirm) return null;

  const handleNextStep = () => {
    setCurrentStep(2);
  };

  const handleGoBack = () => {
    setCurrentStep(1);
  };

  const handleClose = () => {
    setCurrentStep(1);
    setShowConfirm(false);
    setFormData({
      name: '',
      description: '',
      categoryId: '',
      startingFrom: '',
      price: '',
      motivation: '',
    });
    setSubmitError(null);
    setFieldErrors({});
    onClose();
  };

  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);

    const newErrors = { ...fieldErrors };
    if (newFormData.description && newFormData.description.length > 255) {
      newErrors.description = 'Description cannot exceed 255 characters.';
    } else {
      delete newErrors.description;
    }

    if (newFormData.motivation && newFormData.motivation.length > 255) {
      newErrors.motivation = 'Motivation cannot exceed 255 characters.';
    } else {
      delete newErrors.motivation;
    }

    setFieldErrors(newErrors);
  };

  const validateForm = () => {
    setSubmitError(null);

    if (!formData.name?.trim() || !formData.description?.trim() || !formData.categoryId || !formData.startingFrom || !formData.motivation?.trim()) {
      setSubmitError('Please fill in all required fields.');
      return false;
    }

    if (formData.description && formData.description.length > 255) {
      setSubmitError('Description cannot exceed 255 characters.');
      return false;
    }

    if (formData.motivation && formData.motivation.length > 255) {
      setSubmitError('Motivation cannot exceed 255 characters.');
      return false;
    }

    return true;
  };

  const handleOpenConfirm = () => {
    if (validateForm()) {
      setShowConfirm(true);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowConfirm(false);
      return;
    }

    const data = {
      assetName: formData.name.trim(),
      assetCategoryId: formData.categoryId,
      loanDate: new Date(formData.startingFrom).toISOString(),
      assetPrice: formData.price !== '' && formData.price != null ? Number(formData.price) : null,
      assetDescription: formData.description.trim(),
      motivation: formData.motivation.trim(),
      InInventory: false,
    };

    try {
      setSubmitting(true);
      await postAssetRequest(data);
      if (selectedUser?.myportalUserId) {
        dispatch(fetchAssetRequests(selectedUser.myportalUserId, 0, takeRequests || 20));
      }
      toast.success('Successfully sent request!', CREATE_ASSET_REQUEST_TOAST_MESSAGES.success);
      handleClose();
    } catch (e) {
      let errorMessage = 'Failed to submit the request. Please try again.';
      if (e?.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e?.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      setSubmitError(errorMessage);
      toast.error(errorMessage, CREATE_ASSET_REQUEST_TOAST_MESSAGES.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          paddingTop: '60px',
          paddingBottom: '60px',
        }}>
        <div className="modal-dialog modal-lg" style={{ width: '90%', maxWidth: '900px' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Request New Asset - Step {currentStep}</h5>
            </div>
            <div className="modal-body" style={{ minHeight: '350px' }}>
              {currentStep === 1 ? (
                <RequestAssetForm formData={formData} onFormDataChange={handleFormDataChange} fieldErrors={fieldErrors} />
              ) : (
                <div className="mb-3">
                  <label htmlFor="requestDescription" className="form-label">
                    Motivation <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="requestDescription"
                    className={`form-control ${fieldErrors.motivation ? 'is-invalid' : ''}`}
                    rows="7"
                    placeholder="Please tell us why you want this asset."
                    value={formData.motivation || ''}
                    onChange={(e) => handleFormDataChange({ ...formData, motivation: e.target.value })}
                    required
                  />
                  {fieldErrors.motivation && <div className="invalid-feedback">{fieldErrors.motivation}</div>}
                </div>
              )}
              {currentStep === 2 && submitError && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  {submitError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={currentStep === 1 ? handleClose : handleGoBack}
                style={{ width: '150px' }}
                disabled={submitting}>
                {currentStep === 1 ? 'Cancel' : 'Go Back'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={currentStep === 1 ? handleNextStep : handleOpenConfirm}
                style={{ width: '150px' }}
                disabled={submitting}>
                {currentStep === 1 ? 'Next Step' : submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        id="confirm-new-asset-request"
        keepOpen={showConfirm}
        backdrop
        title="Confirm Request"
        confirmText="Yes"
        cancelText="No"
        confirmHandler={handleSubmit}
        closeHandler={handleCancelConfirm}>
        <div>
          <p className="modal-message text-center">Are you sure you want to submit this asset request? The request cannot be modified after submission.</p>
        </div>
      </Modal>
    </>
  );
}

export default RequestAssetModal;
